import { useEffect, useMemo, useState } from "react";
import { format } from "date-fns";
import { useNavigate } from "@tanstack/react-router";
import {
  CircleDollarSign,
  Plus,
  TrendingDown,
  TrendingUp,
  Wallet,
} from "lucide-react";
import { toast } from "sonner";

import { AutoBreadcrumb } from "@/components/ui/auto-breadcrumb";
import { Button } from "@/components/ui/button";
import { getBooks } from "@/features/portfolio/services/portfolio-service";
import { useBaseCurrency } from "@/hooks/use-base-currency";
import { cn } from "@/lib/utils";

import {
  createBudgetRule,
  deleteBudgetRule,
  getBudgetOverview,
  getBudgetReviews,
  getBudgetRules,
  getFiatAssets,
  resolveBudgetReview,
  updateBudgetRule,
} from "../services/budget-service";

import type {
  Asset,
  Book,
  BudgetOverview,
  BudgetReviewItem,
  BudgetReviewResolution,
  BudgetRule,
  BudgetRuleKind,
} from "@/schemas";

import { buildBudgetPlan } from "../lib/build-budget-plan";
import { categoryLabel, groupRulesByKind, monthToString } from "../lib/budget-helpers";
import { DEFAULT_FORM, type BudgetFormState } from "../types";

import { BudgetAlertsBanner } from "./budget-alerts-banner";
import { BudgetFlowCard } from "./budget-flow-card";
import { BudgetItemsCard } from "./budget-items-card";
import { BudgetOccurrencesCard } from "./budget-occurrences-card";
import { BudgetReviewQueueCard } from "./budget-review-queue-card";
import { BudgetSummaryCard } from "./budget-summary-card";
import { RuleDialog } from "./rule-dialog";

export default function BudgetPage() {
  const navigate = useNavigate();
  const { code: currencyCode } = useBaseCurrency();
  const currentMonth = useMemo(() => monthToString(new Date()), []);
  const currentMonthLabel = useMemo(() => format(new Date(), "MMMM yyyy"), []);
  const [overview, setOverview] = useState<BudgetOverview | null>(null);
  const [rules, setRules] = useState<BudgetRule[]>([]);
  const [reviews, setReviews] = useState<BudgetReviewItem[]>([]);
  const [books, setBooks] = useState<Book[]>([]);
  const [fiatAssets, setFiatAssets] = useState<Asset[]>([]);
  const [form, setForm] = useState<BudgetFormState>(DEFAULT_FORM);
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fiatById = useMemo(
    () => new Map(fiatAssets.map((asset) => [asset.asset_id, asset.symbol])),
    [fiatAssets],
  );
  const booksById = useMemo(
    () => new Map(books.map((book) => [book.book_id, book])),
    [books],
  );
  const budgetPlan = useMemo(
    () => buildBudgetPlan(rules, books),
    [rules, books],
  );
  const incomeGroups = useMemo(
    () => groupRulesByKind(rules, "income"),
    [rules],
  );
  const investmentGroups = useMemo(
    () => groupRulesByKind(rules, "investment"),
    [rules],
  );
  const expenseGroups = useMemo(
    () => groupRulesByKind(rules, "expense"),
    [rules],
  );
  const categoryOptionsByKind = useMemo(
    () => ({
      income: Array.from(new Set(incomeGroups.map((group) => group.name))),
      investment: Array.from(
        new Set(investmentGroups.map((group) => group.name)),
      ),
      expense: Array.from(new Set(expenseGroups.map((group) => group.name))),
    }),
    [incomeGroups, investmentGroups, expenseGroups],
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const [overviewData, rulesData, booksData, fiatData, reviewData] =
        await Promise.all([
          getBudgetOverview(currentMonth),
          getBudgetRules(),
          getBooks(),
          getFiatAssets(),
          getBudgetReviews(currentMonth),
        ]);

      setOverview(overviewData);
      setRules(rulesData);
      setBooks(booksData);
      setFiatAssets(fiatData);
      setReviews(reviewData);
      setForm((current) => ({
        ...current,
        currencyAssetId: current.currencyAssetId || fiatData[0]?.asset_id || "",
      }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [currentMonth]);

  const openCreateDialog = (kind: BudgetRuleKind, category?: string) => {
    setEditingRuleId(null);
    setForm({
      ...DEFAULT_FORM,
      kind,
      category: category || "",
      currencyAssetId: fiatAssets[0]?.asset_id || "",
    });
    setIsDialogOpen(true);
  };

  const handleEditRule = (rule: BudgetRule) => {
    setEditingRuleId(rule.id);
    setForm({
      kind: rule.kind,
      label: rule.label,
      category: rule.category,
      amount: `${rule.amount}`,
      currencyAssetId: rule.currency_asset_id,
      dayOfMonth: `${rule.day_of_month || 1}`,
      linkedBookId: rule.linked_book_id || "",
      enabled: rule.enabled,
      notes: rule.notes || "",
    });
    setIsDialogOpen(true);
  };

  const buildPayload = () => ({
    kind: form.kind,
    label: form.label.trim(),
    category: categoryLabel(form.category),
    amount: Number.parseFloat(form.amount),
    currency_asset_id: form.currencyAssetId,
    recurrence: "monthly" as const,
    day_of_month: Number.parseInt(form.dayOfMonth, 10),
    linked_book_id:
      form.kind === "investment" ? form.linkedBookId || undefined : undefined,
    enabled: form.enabled,
    notes: form.notes.trim() || undefined,
  });

  const handleSubmit = async () => {
    if (!form.label.trim() || !form.amount || !form.currencyAssetId) {
      toast.error("Please complete the required fields.");
      return;
    }

    const dayOfMonth = Number.parseInt(form.dayOfMonth, 10);
    if (Number.isNaN(dayOfMonth) || dayOfMonth < 1 || dayOfMonth > 31) {
      toast.error("Day of month must be between 1 and 31.");
      return;
    }

    if (form.kind === "investment" && !form.linkedBookId) {
      toast.error("An investment rule must be linked to a portfolio.");
      return;
    }

    setSaving(true);
    try {
      if (editingRuleId) {
        await updateBudgetRule(editingRuleId, buildPayload());
        toast.success("Budget rule updated");
      } else {
        await createBudgetRule(buildPayload());
        toast.success("Budget rule created");
      }

      setIsDialogOpen(false);
      await loadData();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to save budget rule",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    try {
      await deleteBudgetRule(ruleId);
      if (editingRuleId === ruleId) {
        setIsDialogOpen(false);
      }
      toast.success("Budget rule deleted");
      await loadData();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to delete budget rule",
      );
    }
  };

  const handleResolveReview = async (
    reviewId: string,
    resolution: BudgetReviewResolution,
  ) => {
    try {
      await resolveBudgetReview(reviewId, resolution);
      toast.success("Budget review resolved");
      await loadData();
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : "Failed to resolve review",
      );
    }
  };

  const handleBookNodeClick = (payload: { id?: string }) => {
    if (!payload?.id?.startsWith("book:")) {
      return;
    }

    void navigate({
      to: "/portfolio",
      search: {
        bookId: payload.id.replace("book:", ""),
        tab: "transactions",
      },
    });
  };

  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-auto p-4 md:p-8">
      <div className="flex flex-col gap-4">
        <AutoBreadcrumb />
        <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
          <div className="space-y-1">
            <h1 className="text-3xl font-bold tracking-tight">Budget Plan</h1>
            <p className="text-muted-foreground">
              Manage your monthly budget, view automatic deposits, and review
              pending transactions.
            </p>
          </div>
          <Button onClick={() => openCreateDialog("expense")}>
            <Plus className="mr-2 h-4 w-4" />
            Add budget item
          </Button>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <BudgetSummaryCard
          title="Income"
          value={budgetPlan.totalIncome}
          currencyCode={currencyCode}
          icon={<TrendingUp className="h-4 w-4 text-emerald-500" />}
        />
        <BudgetSummaryCard
          title="Expenses"
          value={budgetPlan.totalExpenses}
          currencyCode={currencyCode}
          icon={<TrendingDown className="h-4 w-4 text-rose-500" />}
        />
        <BudgetSummaryCard
          title="Investments"
          value={budgetPlan.totalInvestments}
          currencyCode={currencyCode}
          icon={<Wallet className="h-4 w-4 text-sky-500" />}
        />
        <BudgetSummaryCard
          title="Remaining"
          value={budgetPlan.remainingToAllocate}
          currencyCode={currencyCode}
          icon={
            <CircleDollarSign
              className={cn(
                "h-4 w-4",
                budgetPlan.remainingToAllocate >= 0
                  ? "text-violet-500"
                  : "text-amber-500",
              )}
            />
          }
        />
      </div>

      <BudgetAlertsBanner alerts={overview?.alerts ?? []} />

      <BudgetFlowCard
        loading={loading}
        nodes={budgetPlan.nodes}
        links={budgetPlan.links}
        currencyCode={currencyCode}
        onBookNodeClick={handleBookNodeClick}
      />

      <div className="grid gap-6 xl:grid-cols-12">
        <div className="xl:col-span-8">
          <BudgetItemsCard
            incomeGroups={incomeGroups}
            investmentGroups={investmentGroups}
            expenseGroups={expenseGroups}
            currencyCode={currencyCode}
            fiatById={fiatById}
            booksById={booksById}
            onOpenCreate={openCreateDialog}
            onEditRule={handleEditRule}
            onDeleteRule={handleDeleteRule}
          />
        </div>

        <div className="flex flex-col gap-6 xl:col-span-4">
          <BudgetOccurrencesCard
            currentMonthLabel={currentMonthLabel}
            overview={overview}
            rules={rules}
            currencyCode={currencyCode}
            onEditRule={handleEditRule}
            onDeleteRule={handleDeleteRule}
          />
          <BudgetReviewQueueCard
            reviews={reviews}
            currencyCode={currencyCode}
            onResolve={(id, resolution) => void handleResolveReview(id, resolution)}
          />
        </div>
      </div>

      <RuleDialog
        open={isDialogOpen}
        saving={saving}
        form={form}
        books={books}
        fiatAssets={fiatAssets}
        categoryOptions={categoryOptionsByKind[form.kind]}
        editingRuleId={editingRuleId}
        onOpenChange={setIsDialogOpen}
        onSubmit={() => void handleSubmit()}
        onFormChange={setForm}
      />
    </div>
  );
}
